<template>
	<form
		novalidate
		@submit.prevent="onSubmit"
		class="d-flex flex-column h-100"
		data-testid="laic-form"
	>
		<div class="modal-body">
			<h5>How may we assist you?</h5>
			<div class="mb-3">
				<label
					for="first-name"
					class="form-label"
					:class="firstNameError ? 'text-danger' : ''"
					data-testid="first-name-label"
				>
					First Name<sup aria-hidden="true">*</sup>
				</label>
				<input
					required
					id="first-name"
					class="form-control"
					:class="firstNameError ? 'is-invalid' : ''"
					:aria-invalid="firstNameError ? 'true' : 'false'"
					data-testid="first-name-input"
					type="text"
					aria-describedby="first-name-error"
					v-model="firstName"
					@blur="firstNameBlur($event, true)"
				/>
				<div id="first-name-error" class="invalid-feedback mt-1" data-testid="first-name-error">
					{{ firstNameError }}
				</div>
			</div>
			<div class="mb-3">
				<label
					for="last-name"
					class="form-label"
					:class="lastNameError ? 'text-danger' : ''"
					data-testid="last-name-label"
				>
					Last Name<sup aria-hidden="true">*</sup>
				</label>
				<input
					required
					id="last-name"
					class="form-control"
					:class="lastNameError ? 'is-invalid' : ''"
					:aria-invalid="lastNameError ? 'true' : 'false'"
					data-testid="last-name-input"
					type="text"
					aria-describedby="last-name-error"
					v-model="lastName"
					@blur="lastNameBlur($event, true)"
				/>
				<div id="last-name-error" class="invalid-feedback mt-1" data-testid="last-name-error">
					{{ lastNameError }}
				</div>
			</div>
			<div class="mb-3">
				<label
					for="email"
					class="form-label"
					:class="emailError ? 'text-danger' : ''"
					data-testid="email-label"
				>
					Email<sup aria-hidden="true">*</sup>
				</label>
				<input
					required
					id="email"
					class="form-control"
					:class="emailError ? 'is-invalid' : ''"
					:aria-invalid="emailError ? 'true' : 'false'"
					data-testid="email-input"
					type="email"
					aria-describedby="email-error"
					v-model="email"
					@blur="emailBlur($event, true)"
				/>
				<div id="email-error" class="invalid-feedback mt-1" data-testid="email-error">
					{{ emailError }}
				</div>
			</div>
			<div class="small">
				By proceeding, I acknowledge and agree to the
				<a href="https://www.lifetime.life/policy/privacy-policy.html">Privacy Policy</a> and
				<a href="https://www.lifetime.life/policy/terms-of-use.html">Terms of Use</a>
			</div>
		</div>
		<div class="modal-footer">
			<div class="d-flex justify-content-end">
				<button class="btn btn-dark" data-testid="submit-btn" type="submit">Next</button>
			</div>
		</div>
	</form>
</template>

<script setup lang="ts">
import { useField, useForm } from 'vee-validate';
import { validateEmail, validateName } from '@/util/validators';
import type { LaicChatForm, UserData } from 'Types/aemui/laic-chat/laicChat';

const props = withDefaults(
	defineProps<{
		userData: UserData | null;
	}>(),
	{
		userData: null
	}
);

const emit = defineEmits<{
	(e: 'form-submitted', userData: UserData): void;
}>();

const { handleSubmit } = useForm<LaicChatForm>({
	initialValues: {
		firstName: props.userData?.firstName ?? '',
		lastName: props.userData?.lastName ?? '',
		email: props.userData?.email ?? ''
	}
});

const {
	errorMessage: firstNameError,
	handleBlur: firstNameBlur,
	value: firstName
} = useField<string>('firstName', (value: string) =>
	validateName(value, 'Please enter your first name')
);

const {
	errorMessage: lastNameError,
	handleBlur: lastNameBlur,
	value: lastName
} = useField<string>('lastName', (value: string) =>
	validateName(value, 'Please enter your last name')
);

const {
	errorMessage: emailError,
	handleBlur: emailBlur,
	value: email
} = useField<string>('email', (value: string) =>
	validateEmail(value, 'Please enter an email address')
);

const onSubmit = handleSubmit(async () => {
	emit('form-submitted', {
		firstName: firstName.value,
		lastName: lastName.value,
		email: email.value
	});
});
</script>
